import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, ArrowRightLeft, Check } from 'lucide-react';
import { previewMigration, useTarget } from '../../api/queries';
import { IconChip, PageHeader } from '../../components/PageHeader';
import { useDebounced } from '../../hooks/useDebounced';
import { DestinationStep } from './wizard/DestinationStep';
import {
  WIZARD_STEPS,
  destinationDataConflicts,
  hasDestination,
  initialWizardState,
  scopeIsEmpty,
  selectionKey,
  toRequest,
  wizardReducer,
  type PreviewState,
  type WizardState,
} from './wizard/model';
import { ReviewStep } from './wizard/ReviewStep';
import { SafetyStep, useSafetyStatus } from './wizard/SafetyStep';
import { ScopeStep } from './wizard/ScopeStep';
import { SourceStep, defaultDataOf } from './wizard/SourceStep';
import { StrategyStep } from './wizard/StrategyStep';

const PREVIEW_DEBOUNCE_MS = 350;

/** Server preview of the current draft; only plans for the chosen source and destination count. */
function useMigrationPreview(state: WizardState): PreviewState {
  const enabled = hasDestination(state);
  const request = useMemo(() => (enabled ? toRequest(state) : null), [enabled, state]);
  const debounced = useDebounced(request, PREVIEW_DEBOUNCE_MS);
  const q = useQuery({
    queryKey: ['migration-preview', debounced],
    queryFn: ({ signal }) => previewMigration(debounced!, signal),
    enabled: Boolean(debounced),
    placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const { refetch: queryRefetch } = q;
  const refetch = useCallback(() => void queryRefetch(), [queryRefetch]);
  const data = q.data;
  const plan =
    enabled && data && data.source.targetId === state.sourceTargetId && data.destination.environment === state.destEnvironment && data.destination.vps.id === state.destVpsId
      ? data
      : undefined;
  const upToDate = Boolean(plan && !q.isPlaceholderData && JSON.stringify(request) === JSON.stringify(debounced));
  return { plan, upToDate, fetching: q.isFetching || !upToDate, error: q.error, refetch };
}

function StepsHeader({ current, maxReachable, onGo }: { current: number; maxReachable: number; onGo: (i: number) => void }) {
  const step = WIZARD_STEPS[current];
  return (
    <nav aria-label="Migration steps" className="mb-6">
      <ul className="steps steps-horizontal w-full max-md:hidden">
        {WIZARD_STEPS.map((s, i) => (
          <li key={s.id} className={`step ${i <= current ? 'step-primary' : ''}`} data-content={i < current ? '✓' : String(i + 1)} aria-current={i === current ? 'step' : undefined}>
            {i < current && i <= maxReachable ? (
              <button type="button" className="link link-hover text-sm" onClick={() => onGo(i)}>
                {s.label}
              </button>
            ) : (
              <span className={`text-sm ${i === current ? 'font-semibold' : 'text-base-content/60'}`}>{s.label}</span>
            )}
          </li>
        ))}
      </ul>
      <div className="md:hidden" aria-current="step">
        <p className="text-sm">
          <span className="text-base-content/60">
            Step {current + 1} of {WIZARD_STEPS.length}:
          </span>{' '}
          <span className="font-semibold">{step.label}</span>
        </p>
        <progress className="progress progress-primary mt-2 w-full" value={current + 1} max={WIZARD_STEPS.length} aria-hidden="true" />
      </div>
    </nav>
  );
}

const STEP_INTRO: Record<(typeof WIZARD_STEPS)[number]['id'], string> = {
  source: 'Choose the deployment whose data and configuration you want to migrate.',
  destination: 'Move it to another VPS in the same environment, or copy it into another environment of the project.',
  scope: 'Choose the databases, volumes and configuration to copy. Everything is selected by default.',
  strategy: 'Decide how the source behaves while its data is copied.',
  safety: 'Migrations replace data. Make sure every database has a fresh, verified backup before continuing.',
  review: 'Check the plan the server computed, then confirm to start.',
};

export default function MigrationWizardPage() {
  const [params] = useSearchParams();
  const targetParam = params.get('target') ?? '';
  const projectParam = params.get('project') ?? '';
  const [state, dispatch] = useReducer(wizardReducer, { ...initialWizardState, project: projectParam });
  const prefill = useTarget(targetParam, Boolean(targetParam));

  useEffect(() => {
    const t = prefill.data;
    if (!t || state.sourceTargetId) return;
    dispatch({ type: 'set-project', project: t.project.key });
    dispatch({ type: 'set-source', targetId: t.id, environment: t.environment, vpsId: t.vps.id, ...defaultDataOf(t) });
    // Only the first load of the prefilled target seeds the wizard.
  }, [prefill.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = useMigrationPreview(state);
  const plan = preview.plan;

  // The first plan for a source and destination selects everything it lists.
  useEffect(() => {
    if (plan && state.seededFor !== selectionKey(state)) dispatch({ type: 'seed', plan });
  }, [plan, state]);

  const safety = useSafetyStatus(plan?.requiredBackups ?? [], state, (plan?.requiredAcknowledgements ?? []).map((a) => a.id));
  const seeded = state.seededFor === selectionKey(state);

  const validity: { ok: boolean; why?: string }[] = [
    { ok: Boolean(state.sourceTargetId), why: 'Choose a project and the deployment to migrate.' },
    (() => {
      if (!hasDestination(state)) return { ok: false, why: 'Choose a destination environment and VPS different from the source.' };
      if (!plan || !seeded) return { ok: false, why: 'Checking the destination…' };
      if (!plan.agents.source.supported || !plan.agents.destination.supported) return { ok: false, why: 'Both agents must support migrations (migrate.v1). Upgrade the agent first.' };
      return { ok: true };
    })(),
    { ok: Boolean(plan) && !scopeIsEmpty(state, plan), why: 'Select at least one database, volume or configuration item to copy.' },
    {
      ok: Boolean(plan) && (destinationDataConflicts(plan!, state).length === 0 || state.overwriteDestination),
      why: 'The destination already holds data: confirm that it may be replaced, or deselect that data.',
    },
    (() => {
      if (!plan) return { ok: false, why: 'Updating the plan…' };
      if (safety.missingBackups.length > 0) return { ok: false, why: `A verified backup newer than the limit is still needed for: ${safety.missingBackups.map((b) => `${b.database} (${b.side})`).join(', ')}.` };
      if (safety.missingAcks.length > 0) return { ok: false, why: 'Tick every confirmation.' };
      return { ok: true };
    })(),
    { ok: true },
  ];
  const firstInvalid = validity.findIndex((v) => !v.ok);
  const maxReachable = firstInvalid === -1 ? WIZARD_STEPS.length - 1 : firstInvalid;
  const current = Math.min(state.step, maxReachable);
  const stepId = WIZARD_STEPS[current].id;
  // Reachability ignores in-flight previews (so a refresh never throws the user back a step), but Next waits for them.
  const canNext = validity[current].ok && (stepId === 'source' || preview.upToDate);
  const nextWhy = validity[current].ok ? (canNext ? undefined : 'Updating the plan…') : validity[current].why;
  const go = (i: number) => dispatch({ type: 'go', step: i });
  // A step that became invalid (e.g. the source changed) is where the user continues from.
  useEffect(() => {
    if (current !== state.step) dispatch({ type: 'go', step: current });
  }, [current, state.step]);
  const stepProps = { state, dispatch, preview };

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Migrations', to: '/migrations' }, { label: 'New migration' }]}
        icon={<IconChip icon={ArrowRightLeft} tone="primary" size="lg" />}
        title="New migration"
        subtitle="Move a deployment’s databases, volumes and configuration to another VPS, or copy them into another environment."
      />
      <StepsHeader current={current} maxReachable={maxReachable} onGo={go} />
      <section className="card dr-surface" aria-labelledby="wizard-step-title">
        <div className="card-body gap-5 p-4 sm:p-6">
          <div>
            <h2 id="wizard-step-title" className="card-title text-lg">
              {WIZARD_STEPS[current].label}
            </h2>
            <p className="text-sm text-base-content/65">{STEP_INTRO[stepId]}</p>
          </div>
          <div key={stepId} className="animate-rise">
            {stepId === 'source' ? <SourceStep {...stepProps} /> : null}
            {stepId === 'destination' ? <DestinationStep {...stepProps} /> : null}
            {stepId === 'scope' ? <ScopeStep {...stepProps} /> : null}
            {stepId === 'strategy' ? <StrategyStep {...stepProps} /> : null}
            {stepId === 'safety' ? <SafetyStep {...stepProps} /> : null}
            {stepId === 'review' ? (
              <ReviewStep {...stepProps} sourceBackupIds={safety.sourceBackupIds} destBackupIds={safety.destBackupIds} onBackToSafety={() => go(WIZARD_STEPS.findIndex((s) => s.id === 'safety'))} />
            ) : null}
          </div>
          <div className="flex flex-col-reverse gap-3 border-t border-hairline pt-4 sm:flex-row sm:items-center sm:justify-between">
            <button type="button" className="btn btn-ghost" disabled={current === 0} onClick={() => go(current - 1)}>
              <ArrowLeft className="size-4" aria-hidden="true" /> Back
            </button>
            {stepId !== 'review' ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {!canNext && nextWhy ? (
                  <p className="text-sm text-base-content/60 sm:text-right" id="wizard-next-hint" aria-live="polite">
                    {nextWhy}
                  </p>
                ) : null}
                <button type="button" className="btn btn-primary" disabled={!canNext} aria-describedby={!canNext ? 'wizard-next-hint' : undefined} onClick={() => go(current + 1)}>
                  {current === WIZARD_STEPS.length - 2 ? (
                    <>
                      <Check className="size-4" aria-hidden="true" /> Review plan
                    </>
                  ) : (
                    <>
                      Next <ArrowRight className="size-4" aria-hidden="true" />
                    </>
                  )}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}
